import { CloudDownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import Page from '@components/Page/Page'
import {
  VenueBillingMonth,
  VenueBillingMonthDetail,
} from '@models/VenueBilling'
import {
  fetchVenueBillingMonth,
  fetchVenueBillingMonths,
  fetchVenuesExportCsv,
} from '@services/VenueBillingService'
import { Permission } from '@utils/permissions'
import {
  Alert,
  App,
  Button,
  Collapse,
  Descriptions,
  Drawer,
  Flex,
  Skeleton,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { DEFAULT_ERROR } from '../../App'

const { Text, Title } = Typography

const monthLabel = (isoDate: string) => dayjs(isoDate).format('MMMM YYYY')

// Single source of truth for every term shown on this page. Used both for the
// "Definitions" glossary and for the per-column header tooltips.
const TERMS = {
  billableVenue:
    'A venue whose type is not a sub-venue. A billable venue counts toward the client’s bill for the months it exists.',
  subVenue:
    'A venue of type “sub-venue”. Never billed, and excluded from every figure on this page.',
  billableVenues:
    'The number of distinct venues that were billable at any point during the month.',
  billableVenueMonths:
    'The prorated total billed for the month: each venue contributes (its active days ÷ days in the month). A venue billable all month counts as 1.00; half the month as 0.50.',
  proration:
    'Billing is by elapsed time, not a flat headcount. A venue created or deleted mid-month is billed only for the days it existed.',
  hidden:
    'The share of the billable venue-months during which the venue was hidden from the mobile app. Shown for visibility only — it does NOT reduce the amount billed.',
  monthStatus:
    'Frozen = taken from the closed monthly rollup and will not change. Live = computed on the fly from the ledger and may still change. Current (partial) = the month in progress, counting only the days elapsed so far.',
  activeDays:
    'The number of days during the month the venue was billable.',
  hiddenDays:
    'The days within “active days” during which the venue was hidden from the mobile app.',
  visibleNow:
    'Whether the venue is visible in the mobile app right now (its latest state), regardless of how long it was hidden during the month.',
} as const

// Ordered glossary for the "Definitions" panel.
const DEFINITIONS: { term: string; definition: string }[] = [
  { term: 'Billable venue', definition: TERMS.billableVenue },
  { term: 'Sub-venue', definition: TERMS.subVenue },
  { term: 'Billable venues', definition: TERMS.billableVenues },
  { term: 'Billable venue-months', definition: TERMS.billableVenueMonths },
  { term: 'Proration', definition: TERMS.proration },
  { term: 'Of which hidden (hidden venue-months)', definition: TERMS.hidden },
  { term: 'Frozen / Live / Current (partial)', definition: TERMS.monthStatus },
  { term: 'Active days', definition: TERMS.activeDays },
  { term: 'Hidden days', definition: TERMS.hiddenDays },
  { term: 'Visible now', definition: TERMS.visibleNow },
]

const headerWithInfo = (label: string, tip: string) => (
  <Space size={4}>
    {label}
    <Tooltip title={tip}>
      <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
    </Tooltip>
  </Space>
)

const VenueBillingPage = () => {
  const { message } = App.useApp()

  const [months, setMonths] = useState<VenueBillingMonth[]>()
  const [loadingMonths, setLoadingMonths] = useState(true)

  const [detail, setDetail] = useState<VenueBillingMonthDetail>()
  const [detailMonth, setDetailMonth] = useState<string>()
  const [loadingDetail, setLoadingDetail] = useState(false)

  const [exporting, setExporting] = useState(false)

  const downloadVenues = () => {
    setExporting(true)
    fetchVenuesExportCsv()
      .then((csv) => {
        // Prepend a UTF-8 BOM so Excel renders accented venue names correctly.
        const blob = new Blob(['\ufeff', csv], {
          type: 'text/csv;charset=utf-8;',
        })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `venues-${dayjs().format('YYYY-MM-DD')}.csv`
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
      })
      .catch(() => message.error(DEFAULT_ERROR))
      .finally(() => setExporting(false))
  }

  useEffect(() => {
    fetchVenueBillingMonths()
      .then(setMonths)
      .catch(() => message.error(DEFAULT_ERROR))
      .finally(() => setLoadingMonths(false))
  }, [message])

  const openDetail = (row: VenueBillingMonth) => {
    const month = dayjs(row.period_month).format('YYYY-MM')
    setDetailMonth(month)
    setDetail(undefined)
    setLoadingDetail(true)
    fetchVenueBillingMonth(month)
      .then(setDetail)
      .catch(() => message.error(DEFAULT_ERROR))
      .finally(() => setLoadingDetail(false))
  }

  const monthColumns: TableColumnsType<VenueBillingMonth> = [
    {
      title: headerWithInfo('Month', TERMS.monthStatus),
      dataIndex: 'period_month',
      render: (value: string, row) => (
        <Space>
          <Text strong>{monthLabel(value)}</Text>
          {row.is_current && <Tag color="blue">Current (partial)</Tag>}
          {!row.is_current && (
            <Tag color={row.frozen ? 'green' : 'default'}>
              {row.frozen ? 'Frozen' : 'Live'}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: headerWithInfo('Billable venues', TERMS.billableVenues),
      dataIndex: 'distinct_billable_venues',
      align: 'right',
      render: (value: number) => <Text strong>{value}</Text>,
    },
    {
      title: headerWithInfo('Billable venue-months', TERMS.billableVenueMonths),
      dataIndex: 'billable_venue_months',
      align: 'right',
      render: (value: number) => value.toFixed(2),
    },
    {
      title: headerWithInfo('Of which hidden', TERMS.hidden),
      dataIndex: 'hidden_venue_months',
      align: 'right',
      render: (value: number) =>
        value > 0 ? (
          <Text type="warning">{value.toFixed(2)}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ]

  const venueColumns: TableColumnsType<
    NonNullable<VenueBillingMonthDetail['venues']>[number]
  > = [
    {
      title: 'Venue',
      dataIndex: 'venue_name',
      render: (value: string | null, row) => value ?? `#${row.venue_id}`,
    },
    {
      title: 'Type',
      dataIndex: 'type_name',
      render: (value: string | null) => value ?? '—',
    },
    {
      title: headerWithInfo('Active days', TERMS.activeDays),
      dataIndex: 'active_days',
      align: 'right',
      render: (value: number) => value.toFixed(2),
    },
    {
      title: headerWithInfo('Hidden days', TERMS.hiddenDays),
      dataIndex: 'hidden_days',
      align: 'right',
      render: (value: number) =>
        value > 0 ? (
          <Text type="warning">{value.toFixed(2)}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: headerWithInfo('Visible now', TERMS.visibleNow),
      dataIndex: 'is_visible_now',
      align: 'center',
      render: (value: boolean) =>
        value ? <Tag color="green">Visible</Tag> : <Tag color="orange">Hidden</Tag>,
    },
  ]

  return (
    <Page
      permissions={Permission.accessVenueBilling}
      pageTitle={{ title: 'Venue billing', level: 4 }}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Billing basis"
        description="A venue is billable for every month in which it exists and is not a sub-venue. Figures are prorated by active days, so a venue live for half a month counts as 0.5. Hidden-venue figures are informational only and do not change the billed total."
      />

      <Collapse
        ghost
        style={{ marginBottom: 16 }}
        items={[
          {
            key: 'definitions',
            label: 'Definitions',
            children: (
              <Descriptions column={1} bordered size="small">
                {DEFINITIONS.map((d) => (
                  <Descriptions.Item key={d.term} label={d.term}>
                    {d.definition}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            ),
          },
        ]}
      />

      <Flex justify="flex-end" style={{ marginBottom: 16 }}>
        <Button
          icon={<CloudDownloadOutlined />}
          loading={exporting}
          onClick={downloadVenues}
        >
          Download venues (CSV)
        </Button>
      </Flex>

      {loadingMonths ? (
        <Skeleton active />
      ) : (
        <Table
          rowKey="period_month"
          dataSource={months}
          columns={monthColumns}
          pagination={false}
          onRow={(row) => ({
            onClick: () => openDetail(row),
            style: { cursor: 'pointer' },
          })}
        />
      )}

      <Drawer
        width={720}
        open={!!detailMonth}
        onClose={() => setDetailMonth(undefined)}
        title={detailMonth ? monthLabel(`${detailMonth}-01`) : ''}
      >
        {loadingDetail || !detail ? (
          <Skeleton active />
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Space size="large" wrap>
              <Statistic
                label="Billable venues"
                value={String(detail.summary.distinct_billable_venues)}
              />
              <Statistic
                label="Billable venue-months"
                value={detail.summary.billable_venue_months.toFixed(2)}
              />
              <Statistic
                label="Hidden venue-months"
                value={detail.summary.hidden_venue_months.toFixed(2)}
              />
            </Space>

            <Table
              rowKey="venue_id"
              size="small"
              dataSource={detail.venues}
              columns={venueColumns}
              pagination={false}
            />
          </Space>
        )}
      </Drawer>
    </Page>
  )
}

const Statistic = ({ label, value }: { label: string; value: string }) => (
  <Space direction="vertical" size={0}>
    <Text type="secondary">{label}</Text>
    <Title level={3} style={{ margin: 0 }}>
      {value}
    </Title>
  </Space>
)

export default VenueBillingPage
